import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import { GeolocationEventType } from 'src/requester/types';
import {
  IKameleoonData,
  KameleoonData,
  GeolocationInfoType,
  GeolocationDataType,
} from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * Geolocation - a class for creating an instance for geolocation data
 * */
export class GeolocationData implements IKameleoonData {
  public status: TrackingStatus;

  private country: string;
  private region?: string;
  private city?: string;
  private postalCode?: string;
  private coordinates?: [number, number];

  /**
   * @param {GeolocationInfoType} geolocationInfo - an object containing information about geolocation of a visitor
   * @example
   * ```ts
   * // Visitor is located at Triumph Arch in Paris, France
   * const location: GeolocationDataType = {
   *   country: 'France',
   *   region: 'Île-de-France',
   *   city: 'Paris',
   *   postalCode: '75008',
   *   coordinates: [48.8738, 2.295]
   * };
   *
   * const geolocation = new GeolocationData(location);
   * ```
   *
   * */
  constructor({
    country,
    region,
    city,
    postalCode,
    coordinates,
  }: GeolocationInfoType) {
    this.country = country;
    this.region = region;
    this.city = city;
    this.postalCode = postalCode;
    this.coordinates = coordinates;
    this.status = TrackingStatus.Unsent;
  }

  get url(): string {
    const region = this.region
      ? UrlParameter.Region + encodeURIComponent(this.region)
      : '';
    const city = this.city
      ? UrlParameter.City + encodeURIComponent(this.city)
      : '';
    const postalCode = this.postalCode
      ? UrlParameter.PostalCode + encodeURIComponent(this.postalCode)
      : '';
    const coordinates = this.coordinates
      ? UrlParameter.Latitude +
        this.coordinates[0] +
        UrlParameter.Longitude +
        this.coordinates[1]
      : '';

    return (
      UrlEventType.Geolocation +
      UrlParameter.Country +
      encodeURIComponent(this.country) +
      region +
      city +
      postalCode +
      coordinates
    );
  }

  get data(): GeolocationDataType {
    return {
      type: KameleoonData.GeolocationData,
      country: this.country,
      region: this.region,
      city: this.city,
      postalCode: this.postalCode,
      coordinates: this.coordinates,
      status: this.status,
    };
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a GeolocationData instance from raw data
   * @param {GeolocationDataType} data - a raw data
   * @return {GeolocationData} a GeolocationData instance
   * */
  public static _fromRaw(data: GeolocationDataType): GeolocationData {
    const { country, region, city, postalCode, coordinates, status } = data;
    const geolocation = new GeolocationData({
      country,
      region,
      city,
      postalCode,
      coordinates,
    });

    geolocation.status = status;

    return geolocation;
  }

  /**
   * @private
   * @method _fromVisit - an internal method for creating an instance of GeolocationData class from a visit
   * @param {VisitType} visit - a visit
   * @returns {GeolocationData | undefined} an instance of GeolocationData class or undefined if geolocation can not be found among visits
   * */
  public static _fromVisit(visit: VisitType): GeolocationData | undefined {
    if (!visit.geolocationEvents?.length) {
      return undefined;
    }

    let geolocationData: GeolocationEventType['data'] | undefined = undefined;

    for (const geolocationEvent of visit.geolocationEvents) {
      const { data } = geolocationEvent;

      if (data.country) {
        geolocationData = data;
      }
    }

    // --- Note ---
    // We only take `country`, `region`, and `city` from geolocation event ignoring other fields
    // as they are not participating in targeting, and visits data won't be tracked again
    const { country, region, city } = geolocationData || {};

    if (country) {
      if (region && city) {
        return new GeolocationData({ country, region, city });
      } else if (region) {
        return new GeolocationData({ country, region });
      } else {
        return new GeolocationData({ country });
      }
    }
  }
}
